// src/contexts/AuthContext.js
import React, { createContext, useState, useEffect, useCallback } from 'react';
import { onAuthStateChanged, GoogleAuthProvider, signInWithPopup, signInWithRedirect, getRedirectResult, signOut as firebaseSignOut } from 'firebase/auth';
import { auth, db } from '../firebaseConfig';
// Ensure deleteDoc is imported
import { doc, getDoc, setDoc, updateDoc, collection, getDocs, deleteDoc } from 'firebase/firestore';
import { skillData as allSkillDefinitions, defaultPlayerStats } from '../data/data';

// --- Helper Functions ---



const createDefaultPlayerDataStructure = (characterName = "Hero") => {
  const initialInventory = {};
  // New starting items: 5 berries and 1 shiny pebble
  initialInventory['berries'] = 5;
  initialInventory['shinyPebble'] = 1;

  const initialSkills = {};
  for (const skillKey in allSkillDefinitions) {
      initialSkills[skillKey] = { level: 1, xp: 0 };
  }

  return {
      name: characterName,
      health: { current: 100, max: 100 },
      mana: { current: 75, max: 75 },
      energy: { current: 100, max: 100 },
      unlockedTier: 1,
      gold: 50,
      currentLocation: 'home',
      tripInventory: {},
      currentCarryWeight: 0,
      unlockedSeeds: [], // Array of seed keys that have been unlocked for foraging
      equipment: {
          mainHand: null,
          offHand: null, helmet: null, chest: null, pants: null, 
          gloves: null, boots: null, belt: null, necklace: null, 
          ring1: null, ring2: null, back: null,
      },
      inventory: initialInventory,
      lockedItems: {}, // Track which items are locked from selling
      skills: initialSkills,
              stats: {
            strength: 10, dexterity: 10, intelligence: 10, constitution: 10, luck: 0, defense: 0,
            health: { current: 100, max: 100 }, damageMin: 1, damageMax: 2,
        },
      carryCapacity: 100, 
      buildings: {
          furnace: { owned: false, upgradeLevel: 0 }, forge: { owned: false, upgradeLevel: 0 },
          sawmill: { owned: false, upgradeLevel: 0 }, workbench: { owned: false, upgradeLevel: 0 },
          shopFront: { owned: false, upgradeLevel: 0 }, questBoard: { owned: false, upgradeLevel: 0 },
          anvil: { owned: false, upgradeLevel: 0 },
          campfire: { owned: false, upgradeLevel: 0 }, brewingStation: { owned: false, upgradeLevel: 0 },
          tanningStation: { owned: false, upgradeLevel: 0 }, runecraftingStation: { owned: false, upgradeLevel: 0 },
          landPlot: { owned: false, upgradeLevel: 0 }, fletchingStation: { owned: false, upgradeLevel: 0 },
        dungeonEntrance: { owned: false, upgradeLevel: 0 },
          thievingDummy: { owned: false, upgradeLevel: 0 },
      },
      activeQuests: [],
      customerOrders: [],
      day: 1,
      dailyConsumablePurchases: {
          healingPotion: 0,
          energyPotion: 0,
          manaPotion: 0,
          bandage: 0,
          apple: 0,
          berries: 0,
          lastResetDay: 1
      },
      monsterKills: {}, // Track individual monster kills for progression system
      playerStats: defaultPlayerStats,
      completedAchievements: [],
      timeOfDay: 'morning',
      gameTime: { hour: 8, minute: 0 },
      selectedDifficulty: 'medium',
      worldState: { isPaused: false, gameSpeed: 1 },
      settings: { autoSave: true, notifications: true },
      unlockedLocations: ['home', 'wilderness'],
      lastSaveTime: Date.now(),
      createdAt: Date.now(),
      version: '1.0.0'
  };
};
// --- End Helper Functions ---


export const AuthContext = createContext({ 
    currentUser: null,
    authLoading: true,
    isGuest: false,
    userCharacters: [null, null, null],
    isLoadingCharacters: true,
    signInWithGoogle: async () => {},
    signOut: async () => {},
    continueAsGuest: () => {},
    clearGuestMode: () => {},
    fetchUserCharacters: async (userId) => {},
    createCharacter: async (characterName, slotId, extraData = {}) => null,
    deleteCharacter: async (characterSlotId) => false,
    signInError: null,
    clearSignInError: () => {},
});

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [isGuest, setIsGuest] = useState(false);
  const [userCharacters, setUserCharacters] = useState([null, null, null]);
  const [isLoadingCharacters, setIsLoadingCharacters] = useState(true);
  const [signInError, setSignInError] = useState(null);

  const fetchUserCharacters = useCallback(async (userId) => {
    if (!userId) {
      console.log("[AuthContext] fetchUserCharacters: No userId, setting empty characters and finishing loading.");
      setUserCharacters([null, null, null]);
      setIsLoadingCharacters(false);
      return;
    }
    console.log(`[AuthContext] Fetching characters for user ${userId}...`);
    setIsLoadingCharacters(true);
    try {
      const charsColRef = collection(db, "users", userId, "characters");
      const charQuerySnap = await getDocs(charsColRef);
      const fetchedChars = [null, null, null];
      
      console.log(`[AuthContext] Found ${charQuerySnap.docs.length} character documents.`);
      charQuerySnap.forEach(charDoc => {
          const charData = charDoc.data();
          if (charData.slotId !== undefined && charData.slotId >= 0 && charData.slotId < 3) {
              fetchedChars[charData.slotId] = { id: charDoc.id, ...charData };
          } else {
              console.warn(`[AuthContext] Character doc ${charDoc.id} has invalid slotId: ${charData.slotId}`);
          }
      });
      setUserCharacters(fetchedChars);
      console.log("[AuthContext] Set userCharacters:", fetchedChars);
    } catch (error) {
      console.error("[AuthContext] Error fetching characters:", error);
      setUserCharacters([null, null, null]);
    } finally {
      setIsLoadingCharacters(false);
      console.log("[AuthContext] Finished fetching characters. isLoadingCharacters: false");
    }
  }, []); // Removed db from dependencies as it's stable

  useEffect(() => {
    console.log("[AuthContext] Mounting AuthProvider. Checking guest status and subscribing to auth state.");
    const guestStatus = localStorage.getItem('isGuest');
    if (guestStatus === 'true') {
      console.log("[AuthContext] Initializing as Guest from localStorage.");
      setIsGuest(true);
      setCurrentUser(null);
      setUserCharacters([null, null, null]);
      setIsLoadingCharacters(false);
      setAuthLoading(false);
    } else {
      setAuthLoading(true); 
      setIsLoadingCharacters(true); 
    }

    // Check for redirect result on app load
    getRedirectResult(auth)
      .then((result) => {
        if (result) {
          console.log("[AuthContext] Redirect sign-in successful:", result.user.uid);
          // User will be handled by onAuthStateChanged below
        }
      })
      .catch((error) => {
        console.error("[AuthContext] Error getting redirect result:", error);
        // Reset loading states if redirect failed
        if (!guestStatus) {
          setAuthLoading(false);
          setIsLoadingCharacters(false);
        }
      });

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log("[AuthContext] onAuthStateChanged. User:", user ? user.uid : 'null');
      if (user) {
        setCurrentUser(user);
        setIsGuest(false);
        localStorage.removeItem('isGuest');
        
        try {
          const userDocRef = doc(db, "users", user.uid);
          const userDocSnap = await getDoc(userDocRef);
          if (!userDocSnap.exists()) {
            await setDoc(userDocRef, { createdAt: new Date(), lastLogin: new Date() });
            console.log(`[AuthContext] User doc created for ${user.uid}.`);
            setUserCharacters([null,null,null]);
            setIsLoadingCharacters(false); 
          } else {
            await updateDoc(userDocRef, { lastLogin: new Date() });
            console.log(`[AuthContext] User doc updated for ${user.uid}. Now fetching characters.`);
            await fetchUserCharacters(user.uid);
          }
        } catch (error) {
          console.error("[AuthContext] Error with user document in onAuthStateChanged:", error);
          setIsLoadingCharacters(false);
        }
        setAuthLoading(false);
      } else {
        setCurrentUser(null);
        if (localStorage.getItem('isGuest') !== 'true') {
            setIsGuest(false); 
            setUserCharacters([null, null, null]);
            setIsLoadingCharacters(false);
            setAuthLoading(false);
        }
        console.log("[AuthContext] No user signed in.");
      }
    });

    return () => {
      console.log("[AuthContext] Unsubscribing from onAuthStateChanged.");
      unsubscribe();
    };
  }, [fetchUserCharacters]);

  const signInWithGoogle = useCallback(async () => {
    console.log("[AuthContext] signInWithGoogle called.");
    setAuthLoading(true);
    setIsLoadingCharacters(true); 
    setIsGuest(false);
    setSignInError(null);
    localStorage.removeItem('isGuest');
    const provider = new GoogleAuthProvider();
    
    // Add custom parameters to prevent account selection issues
    provider.setCustomParameters({
      prompt: 'select_account'
    });
    
    try {
      // Try popup first
      console.log("[AuthContext] Attempting popup sign-in...");
      await signInWithPopup(auth, provider);
    } catch (popupError) {
      console.warn("[AuthContext] Popup sign-in failed:", popupError.code, popupError.message);
      
      // If popup fails due to popup blocking or network issues, try redirect
      if (popupError.code === 'auth/popup-blocked' || 
          popupError.code === 'auth/network-request-failed' ||
          popupError.code === 'auth/popup-closed-by-user' ||
          popupError.code === 'auth/cancelled-popup-request') {
        try {
          console.log("[AuthContext] Trying redirect sign-in as fallback...");
          setSignInError("Popup blocked. Redirecting to Google sign-in...");
          
          // Add a small delay to show the message
          await new Promise(resolve => setTimeout(resolve, 1000));
          await signInWithRedirect(auth, provider);
          // Don't reset loading states here - redirect will reload the page
          return;
        } catch (redirectError) {
          console.error("[AuthContext] Redirect sign-in also failed:", redirectError);
          setCurrentUser(null);
          setAuthLoading(false);
          setIsLoadingCharacters(false);
          
          // Set specific error messages based on error code
          if (redirectError.code === 'auth/network-request-failed') {
            setSignInError("Network connection failed. Please check your internet connection and try again.");
          } else if (redirectError.code === 'auth/configuration-not-found') {
            setSignInError("Firebase configuration issue. Please try refreshing the page.");
          } else {
            setSignInError(`Sign-in failed: ${redirectError.message}`);
          }
          
          throw redirectError;
        }
      } else {
        // For other errors, don't try redirect
        console.error("[AuthContext] Sign-in failed with non-recoverable error:", popupError);
        setCurrentUser(null);
        setAuthLoading(false);
        setIsLoadingCharacters(false);
        
        // Set specific error messages
        if (popupError.code === 'auth/network-request-failed') {
          setSignInError("Network connection failed. Please check your internet connection and try again.");
        } else if (popupError.code === 'auth/too-many-requests') {
          setSignInError("Too many failed attempts. Please try again later.");
        } else if (popupError.code === 'auth/unauthorized-domain') {
          setSignInError("This domain is not authorized for Google sign-in.");
        } else {
          setSignInError(`Sign-in failed: ${popupError.message}`);
        }
        
        throw popupError;
      }
    }
  }, []);

  const createCharacter = useCallback(async (characterName, slotId, extraData = {}) => {
    if (!currentUser) {
      console.error("[AuthContext] No user for createCharacter.");
      return null;
    }
    console.log(`[AuthContext] Creating char: "${characterName}", slot: ${slotId}`, extraData);
    setIsLoadingCharacters(true);
    try {
      const defaultData = createDefaultPlayerDataStructure(characterName);
      const characterDataToSave = {
        ...defaultData,
        ...extraData, // Merge any extra data like portrait
        slotId: Number(slotId), // Ensure slotId is a number
        name: characterName,
        createdAt: new Date(),
      };
      
      const firestoreDocId = `slot_${slotId}`; // Use consistent ID format
      const charDocRef = doc(db, "users", currentUser.uid, "characters", firestoreDocId);
      await setDoc(charDocRef, characterDataToSave);
      console.log(`[AuthContext] Char "${characterName}" created in Firestore (docId: ${firestoreDocId}).`);
      
      await fetchUserCharacters(currentUser.uid); 
      return { id: firestoreDocId, ...characterDataToSave };
    } catch (error) {
      console.error(`[AuthContext] Error creating character:`, error);
      setIsLoadingCharacters(false);
      return null;
    }
  }, [currentUser, fetchUserCharacters]); // Removed db

  const deleteCharacter = useCallback(async (characterSlotId) => {
    if (!currentUser) {
      console.error("[AuthContext] No user logged in to delete character.");
      return false;
    }
    const characterFirestoreId = `slot_${characterSlotId}`;
    console.log(`[AuthContext] Attempting to delete character in slot ${characterSlotId} (Firestore ID: ${characterFirestoreId}) for user ${currentUser.uid}`);
    setIsLoadingCharacters(true); // Indicate loading during deletion and re-fetch
    try {
      const charDocRef = doc(db, "users", currentUser.uid, "characters", characterFirestoreId);
      await deleteDoc(charDocRef);
      console.log(`[AuthContext] Character ${characterFirestoreId} deleted successfully from Firestore.`);
      await fetchUserCharacters(currentUser.uid); // Re-fetch to update userCharacters state
      return true;
    } catch (error) {
      console.error(`[AuthContext] Error deleting character ${characterFirestoreId}:`, error);
      setIsLoadingCharacters(false); // Ensure loading stops on error
      return false;
    }
  }, [currentUser, fetchUserCharacters]); // Removed db

  const signOut = useCallback(async () => {
    console.log("[AuthContext] signOut called.");
    setAuthLoading(true);
    setIsLoadingCharacters(true);
    try {
      await firebaseSignOut(auth);
      setIsGuest(false);
      localStorage.removeItem('isGuest');
      localStorage.removeItem('playerData_guest');
      // onAuthStateChanged will handle clearing currentUser, userCharacters, and setting loading states
    } catch (error) {
      console.error("[AuthContext] Error signing out: ", error);
      setAuthLoading(false);
      setIsLoadingCharacters(false);
    }
  }, []);

  const continueAsGuest = useCallback(() => {
    console.log("[AuthContext] continueAsGuest called.");
    setAuthLoading(true);
    setCurrentUser(null);
    setIsGuest(true);
    localStorage.setItem('isGuest', 'true');
    setUserCharacters([null, null, null]); 
    setIsLoadingCharacters(false);
    setAuthLoading(false);
  }, []);

  const clearGuestMode = useCallback(() => {
    setIsGuest(false);
    localStorage.removeItem('isGuest');
  }, []);

  const clearSignInError = useCallback(() => {
    setSignInError(null);
  }, []);

  const value = {
    currentUser, authLoading, isGuest, userCharacters, isLoadingCharacters,
    signInWithGoogle, signOut, continueAsGuest, clearGuestMode,
    fetchUserCharacters, createCharacter, deleteCharacter,
    signInError, clearSignInError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};